import React, { useState, useEffect, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { storeService, Store, CatalogueItem, StoreTransferHistory } from '../services/storeService';
import { toast } from 'react-hot-toast';
import { 
  BuildingStorefrontIcon, 
  ArrowsRightLeftIcon, 
  PlusIcon, 
  XCircleIcon,
  MagnifyingGlassIcon,
  GlobeAltIcon,
  ArchiveBoxIcon,
  ClockIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

const StoresPage: React.FC = () => {
  const [stores, setStores] = useState<Store[]>([]);
  const [catalogue, setCatalogue] = useState<CatalogueItem[]>([]);
  const [transfers, setTransfers] = useState<StoreTransferHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'stores' | 'transfers'>('stores');
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Modales
  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false);
  const [editingStore, setEditingStore] = useState<Store | null>(null);
  const [storeForm, setStoreForm] = useState<{
    name: string;
    type: 'PHYSICAL' | 'WAREHOUSE' | 'ONLINE';
    address: string;
    phone: string;
    isVirtual: boolean;
    active: boolean;
  }>({
    name: '',
    type: 'PHYSICAL',
    address: '',
    phone: '',
    isVirtual: false,
    active: true,
  });

  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [transferForm, setTransferForm] = useState({
    fromStoreId: '',
    toStoreId: '',
    productCode: '',
    qrCodeValue: '',
    inventoryType: 'maestro',
    quantity: 1,
    notes: '',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [storesData, transfersData] = await Promise.all([
        storeService.getStores(),
        storeService.getTransfers(),
      ]);
      setStores(storesData.stores);
      setCatalogue(storesData.catalogue);
      setTransfers(transfersData);
      if (storesData.stores.length > 0 && !selectedStoreId) {
        setSelectedStoreId(storesData.stores[0].id);
      }
    } catch (error: any) {
      console.error('Error al cargar datos de tiendas:', error);
      toast.error('Error al cargar datos de las tiendas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const selectedStore = useMemo(() => {
    return stores.find((s) => s.id === selectedStoreId) || stores[0];
  }, [stores, selectedStoreId]);

  // Cálculo e inventario por producto para la tienda seleccionada
  const storeInventory = useMemo(() => {
    if (!selectedStore || !catalogue.length) return [];

    return catalogue.map((item) => {
      const stockObj = selectedStore.stocks?.find(
        (s) => s.productCode === item.cod_producto && s.inventoryType === item.inventoryType
      );

      // Si es el Almacén Principal, mostramos su stock particular (si ya tiene StoreStock) o el stock general inicial
      let qty = 0;
      if (stockObj) {
        qty = stockObj.quantity;
      } else if (selectedStore.type === 'WAREHOUSE') {
        qty = item.generalStock;
      }

      return {
        ...item,
        storeQuantity: qty,
      };
    }).filter((item) => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return item.name.toLowerCase().includes(q) || item.cod_producto.toLowerCase().includes(q);
    });
  }, [selectedStore, catalogue, searchQuery]);

  const handleOpenCreateStore = () => {
    setEditingStore(null);
    setStoreForm({
      name: '',
      type: 'PHYSICAL',
      address: '',
      phone: '',
      isVirtual: false,
      active: true,
    });
    setIsStoreModalOpen(true);
  };

  const handleOpenEditStore = (store: Store) => {
    setEditingStore(store);
    setStoreForm({
      name: store.name,
      type: store.type,
      address: store.address || '',
      phone: store.phone || '',
      isVirtual: store.isVirtual,
      active: store.active,
    });
    setIsStoreModalOpen(true);
  };

  const handleSaveStore = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingStore) {
        await storeService.updateStore(editingStore.id, storeForm);
        toast.success('Tienda actualizada exitosamente');
      } else {
        await storeService.createStore(storeForm);
        toast.success('Tienda creada exitosamente');
      }
      setIsStoreModalOpen(false);
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al guardar la tienda');
    }
  };

  const handleOpenTransfer = (item?: { cod_producto: string; inventoryType: string; qrCodeValue?: string }) => {
    setTransferForm({
      fromStoreId: selectedStore ? selectedStore.id.toString() : stores[0]?.id.toString() || '',
      toStoreId: stores.find((s) => s.id !== selectedStore?.id)?.id.toString() || '',
      productCode: item ? item.cod_producto : catalogue[0]?.cod_producto || '',
      qrCodeValue: item?.qrCodeValue || '',
      inventoryType: item ? item.inventoryType : catalogue[0]?.inventoryType || 'maestro',
      quantity: 1,
      notes: '',
    });
    setIsTransferModalOpen(true);
  };

  const handleExecuteTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await storeService.transferInventory({
        fromStoreId: parseInt(transferForm.fromStoreId),
        toStoreId: parseInt(transferForm.toStoreId),
        productCode: transferForm.productCode,
        qrCodeValue: transferForm.qrCodeValue,
        inventoryType: transferForm.inventoryType,
        quantity: parseInt(transferForm.quantity.toString()),
        notes: transferForm.notes,
      });
      toast.success('Traspaso completado exitosamente');
      setIsTransferModalOpen(false);
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al realizar el traspaso');
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Encabezado Principal */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <BuildingStorefrontIcon className="w-8 h-8 text-indigo-600" />
              Sucursales e Inventario Multitienda
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Gestiona el inventario de tus sucursales, activa/desactiva tiendas y realiza traspasos de prendas fácilmente sin duplicar registros.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleOpenTransfer()}
              className="flex items-center gap-2 px-4 py-2.5 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-all rounded-xl font-semibold text-sm shadow-sm"
            >
              <ArrowsRightLeftIcon className="w-5 h-5" />
              Nuevo Traspaso
            </button>
            <button
              onClick={handleOpenCreateStore}
              className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white transition-all rounded-xl font-semibold text-sm shadow-sm"
            >
              <PlusIcon className="w-5 h-5" />
              Crear Tienda
            </button>
          </div>
        </div>

        {/* Pestañas de Navegación */}
        <div className="flex border-b border-gray-200 mb-8">
          <button
            onClick={() => setActiveTab('stores')}
            className={`flex items-center gap-2 py-4 px-6 font-medium text-sm border-b-2 transition-all ${
              activeTab === 'stores'
                ? 'border-indigo-600 text-indigo-600 font-semibold'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <BuildingStorefrontIcon className="w-5 h-5" />
            Gestión de Tiendas e Inventario
          </button>
          <button
            onClick={() => setActiveTab('transfers')}
            className={`flex items-center gap-2 py-4 px-6 font-medium text-sm border-b-2 transition-all ${
              activeTab === 'transfers'
                ? 'border-indigo-600 text-indigo-600 font-semibold'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <ClockIcon className="w-5 h-5" />
            Historial de Traspasos
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : activeTab === 'stores' ? (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Lista de Tiendas (Sidebar Izquierdo) */}
            <div className="lg:col-span-1 space-y-4">
              <h2 className="text-lg font-bold text-gray-900 px-1">Tus Sucursales</h2>
              {stores.map((store) => {
                const isSelected = store.id === selectedStore?.id;
                return (
                  <div
                    key={store.id}
                    onClick={() => setSelectedStoreId(store.id)}
                    className={`p-5 rounded-2xl cursor-pointer transition-all border ${
                      isSelected
                        ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200 border-indigo-600'
                        : 'bg-white text-gray-800 hover:bg-gray-50 border-gray-100 shadow-sm'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {store.type === 'WAREHOUSE' ? (
                          <ArchiveBoxIcon className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-indigo-600'}`} />
                        ) : store.isVirtual ? (
                          <GlobeAltIcon className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-emerald-600'}`} />
                        ) : (
                          <BuildingStorefrontIcon className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-blue-600'}`} />
                        )}
                        <span className="font-bold text-base tracking-tight">{store.name}</span>
                      </div>
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                          store.active
                            ? isSelected
                              ? 'bg-indigo-500 text-white border border-indigo-400'
                              : 'bg-emerald-100 text-emerald-700'
                            : 'bg-rose-100 text-rose-700'
                        }`}
                      >
                        {store.active ? 'Activa' : 'Inactiva'}
                      </span>
                    </div>
                    <p className={`text-xs mb-4 line-clamp-1 ${isSelected ? 'text-indigo-100' : 'text-gray-500'}`}>
                      {store.address || 'Sin dirección registrada'}
                    </p>
                    <div className="flex items-center justify-between border-t pt-3 border-current/10">
                      <span className={`text-xs font-medium ${isSelected ? 'text-indigo-100' : 'text-gray-500'}`}>
                        {store.type === 'WAREHOUSE' ? 'Almacén General' : store.isVirtual ? 'Tienda Virtual' : 'Tienda Física'}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditStore(store);
                        }}
                        className={`text-xs font-semibold px-2.5 py-1 rounded-lg transition-all ${
                          isSelected
                            ? 'bg-white text-indigo-600 hover:bg-indigo-50'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Configurar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Inventario de la Tienda Seleccionada */}
            <div className="lg:col-span-3 space-y-6">
              {selectedStore && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 pb-6 border-b border-gray-100">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">
                        Inventario en: <span className="text-indigo-600">{selectedStore.name}</span>
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">
                        {selectedStore.type === 'WAREHOUSE'
                          ? 'Muestra el inventario raíz disponible en el almacén principal.'
                          : 'Muestra las existencias asignadas específicamente a esta sucursal.'}
                      </p>
                    </div>
                    {/* Buscador */}
                    <div className="relative w-full sm:w-72">
                      <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Buscar prenda o código..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-11 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                      />
                    </div>
                  </div>

                  {/* Tabla de Productos */}
                  <div className="overflow-x-auto custom-scrollbar">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b border-gray-100 text-gray-500 text-xs font-semibold uppercase tracking-wider">
                          <th className="py-3 px-4">Código</th>
                          <th className="py-3 px-4">Descripción</th>
                          <th className="py-3 px-4">Tipo Inventario</th>
                          <th className="py-3 px-4 text-center">Stock en Sucursal</th>
                          <th className="py-3 px-4 text-right">Acción</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 text-sm text-gray-700">
                        {storeInventory.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="py-12 text-center text-gray-500">
                              No se encontraron prendas con ese criterio de búsqueda.
                            </td>
                          </tr>
                        ) : (
                          storeInventory.map((item) => (
                            <tr key={`${item.inventoryType}-${item.cod_producto}`} className="hover:bg-gray-50/50 transition-colors">
                              <td className="py-3.5 px-4 font-semibold text-gray-900">{item.cod_producto}</td>
                              <td className="py-3.5 px-4">{item.name}</td>
                              <td className="py-3.5 px-4">
                                <span className={`text-[10px] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider ${
                                  item.inventoryType === 'novias' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                                }`}>
                                  {item.inventoryType}
                                </span>
                              </td>
                              <td className="py-3.5 px-4 text-center">
                                <span className={`inline-flex items-center justify-center px-3 py-1 rounded-lg font-bold text-sm ${
                                  item.storeQuantity > 0
                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                    : 'bg-gray-100 text-gray-500'
                                }`}>
                                  {item.storeQuantity}
                                </span>
                              </td>
                              <td className="py-3.5 px-4 text-right">
                                <button
                                  onClick={() => handleOpenTransfer(item)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 hover:border-indigo-600 hover:text-indigo-600 text-gray-600 transition-all rounded-lg font-medium text-xs shadow-sm"
                                >
                                  <ArrowsRightLeftIcon className="w-4 h-4" />
                                  Traspasar
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Historial de Traspasos */
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Historial de Traspasos de Inventario</h2>
            <div className="overflow-x-auto custom-scrollbar">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 text-gray-500 text-xs font-semibold uppercase tracking-wider">
                    <th className="py-3 px-4">Fecha</th>
                    <th className="py-3 px-4">Origen → Destino</th>
                    <th className="py-3 px-4">Prenda</th>
                    <th className="py-3 px-4 text-center">Cantidad</th>
                    <th className="py-3 px-4">Realizado Por</th>
                    <th className="py-3 px-4">Notas</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm text-gray-700">
                  {transfers.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-gray-500">
                        No hay traspasos registrados en el historial.
                      </td>
                    </tr>
                  ) : (
                    transfers.map((t) => (
                      <tr key={t.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="py-4 px-4 whitespace-nowrap text-gray-500 text-xs">
                          {new Date(t.createdAt).toLocaleString('es-MX', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td className="py-4 px-4 font-medium">
                          <span className="text-gray-600">{t.fromStore?.name}</span>
                          <span className="mx-2 text-indigo-600 font-bold">→</span>
                          <span className="text-gray-900 font-semibold">{t.toStore?.name}</span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="font-semibold text-gray-900">{t.productCode}</div>
                          <div className="text-xs text-gray-500">{t.productName}</div>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className="px-3 py-1 bg-indigo-50 text-indigo-700 font-bold rounded-lg text-sm">
                            {t.quantity}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-gray-600">
                          {t.user?.nombre_completo || t.user?.username || 'Sistema'}
                        </td>
                        <td className="py-4 px-4 text-gray-500 text-xs italic">
                          {t.notes || 'Sin notas'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modal Crear / Editar Tienda */}
        {isStoreModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingStore ? 'Configurar Sucursal' : 'Nueva Sucursal'}
                </h3>
                <button
                  onClick={() => setIsStoreModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-full hover:bg-gray-100"
                >
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>
              <form onSubmit={handleSaveStore} className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nombre de la Tienda</label>
                  <input
                    type="text"
                    required
                    placeholder="Ej. Tienda Esquina / Sucursal Centro"
                    value={storeForm.name}
                    onChange={(e) => setStoreForm({ ...storeForm, name: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Tipo de Establecimiento</label>
                    <select
                      value={storeForm.type}
                      onChange={(e) => setStoreForm({ ...storeForm, type: e.target.value as any })}
                      className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                    >
                      <option value="PHYSICAL">Tienda Física</option>
                      <option value="WAREHOUSE">Almacén General</option>
                      <option value="ONLINE">Tienda Online</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">¿Es Tienda Virtual?</label>
                    <div className="flex items-center h-11">
                      <label className="relative flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={storeForm.isVirtual}
                          onChange={(e) => setStoreForm({ ...storeForm, isVirtual: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                        <span className="ml-3 text-sm font-medium text-gray-700">
                          {storeForm.isVirtual ? 'Sí (Envíos)' : 'No (Física)'}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Dirección</label>
                  <input
                    type="text"
                    placeholder="Ubicación o notas de la sucursal..."
                    value={storeForm.address}
                    onChange={(e) => setStoreForm({ ...storeForm, address: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Teléfono de Contacto</label>
                  <input
                    type="text"
                    placeholder="Teléfono de la sucursal..."
                    value={storeForm.phone}
                    onChange={(e) => setStoreForm({ ...storeForm, phone: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                  />
                </div>

                {editingStore && (
                  <div className="border-t border-gray-100 pt-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Estado de Operación</label>
                    <label className="relative flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={storeForm.active}
                        onChange={(e) => setStoreForm({ ...storeForm, active: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                      <span className="ml-3 text-sm font-medium text-gray-700">
                        {storeForm.active ? 'Tienda Activa' : 'Tienda Desactivada / Pausada'}
                      </span>
                    </label>
                  </div>
                )}

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setIsStoreModalOpen(false)}
                    className="px-5 py-2.5 text-gray-600 hover:bg-gray-100 transition-colors rounded-xl text-sm font-semibold"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white transition-all rounded-xl text-sm font-semibold shadow-md shadow-indigo-100"
                  >
                    {editingStore ? 'Guardar Cambios' : 'Crear Sucursal'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Traspaso de Inventario */}
        {isTransferModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <ArrowsRightLeftIcon className="w-6 h-6 text-indigo-600" />
                  Traspaso de Prendas entre Tiendas
                </h3>
                <button
                  onClick={() => setIsTransferModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-full hover:bg-gray-100"
                >
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>
              <form onSubmit={handleExecuteTransfer} className="p-6 space-y-5">
                <div className="bg-indigo-50/50 border border-indigo-100 rounded-2xl p-4 flex items-start gap-3">
                  <InformationCircleIcon className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-indigo-900 leading-relaxed">
                    Los artículos seleccionados se transferirán entre las sucursales haciendo referencia a su registro en el catálogo general. No se crearán duplicados en el sistema.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Tienda Origen</label>
                    <select
                      required
                      value={transferForm.fromStoreId}
                      onChange={(e) => setTransferForm({ ...transferForm, fromStoreId: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-medium text-gray-900"
                    >
                      <option value="">Selecciona origen...</option>
                      {stores.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name} {s.type === 'WAREHOUSE' ? '(Almacén Principal)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Tienda Destino</label>
                    <select
                      required
                      value={transferForm.toStoreId}
                      onChange={(e) => setTransferForm({ ...transferForm, toStoreId: e.target.value })}
                      className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-medium text-gray-900"
                    >
                      <option value="">Selecciona destino...</option>
                      {stores
                        .filter((s) => s.id.toString() !== transferForm.fromStoreId)
                        .map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Prenda a Traspasar</label>
                  <select
                    required
                    value={`${transferForm.inventoryType}|${transferForm.productCode}`}
                    onChange={(e) => {
                      const [invType, pCode] = e.target.value.split('|');
                      const selectedItem = catalogue.find((c) => c.cod_producto === pCode && c.inventoryType === invType);
                      setTransferForm({
                        ...transferForm,
                        inventoryType: invType,
                        productCode: pCode,
                        qrCodeValue: selectedItem?.qrCodeValue || '',
                      });
                    }}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-medium text-gray-900"
                  >
                    {catalogue.map((item) => (
                      <option key={`${item.inventoryType}-${item.cod_producto}`} value={`${item.inventoryType}|${item.cod_producto}`}>
                        [{item.cod_producto}] {item.name} ({item.inventoryType})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Cantidad</label>
                  <input
                    type="number"
                    min="1"
                    required
                    value={transferForm.quantity}
                    onChange={(e) => setTransferForm({ ...transferForm, quantity: parseInt(e.target.value) || 1 })}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-bold text-indigo-600"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Notas / Motivo del Traspaso (Opcional)</label>
                  <textarea
                    rows={3}
                    placeholder="Ej. Traspaso solicitado por inventario bajo en sucursal..."
                    value={transferForm.notes}
                    onChange={(e) => setTransferForm({ ...transferForm, notes: e.target.value })}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                  />
                </div>

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setIsTransferModalOpen(false)}
                    className="px-5 py-2.5 text-gray-600 hover:bg-gray-100 transition-colors rounded-xl text-sm font-semibold"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white transition-all rounded-xl text-sm font-semibold shadow-md shadow-indigo-100 flex items-center gap-2"
                  >
                    <ArrowsRightLeftIcon className="w-4 h-4" />
                    Ejecutar Traspaso
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StoresPage;
